import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiError } from '../api/client';

interface SftpInfo {
  host: string | null;
  port: number;
  username: string;
}

interface RotateResponse extends SftpInfo {
  private_key_openssh: string;
}

export function SettingsSftpPage() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const [rotated, setRotated] = useState<RotateResponse | null>(null);
  const [rotating, setRotating] = useState(false);
  const [rotateError, setRotateError] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);

  const { data, isLoading, error } = useQuery<SftpInfo, ApiError>({
    queryKey: ['me-sftp'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return apiRequest<SftpInfo>('/api/v1/me/sftp', { token });
    },
    retry: false,
  });

  async function rotate() {
    setRotateError(null);
    setRotating(true);
    try {
      const token = await getAccessTokenSilently();
      const resp = await apiRequest<RotateResponse>('/api/v1/me/sftp/rotate-key', {
        token,
        method: 'POST',
      });
      setRotated(resp);
      await queryClient.invalidateQueries({ queryKey: ['me-sftp'] });
    } catch (err) {
      setRotateError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setRotating(false);
    }
  }

  if (isLoading) return <div>Loading…</div>;

  if (error) {
    return (
      <div className="rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-card)] p-6">
        <h1 className="text-xl font-semibold">No SFTP account</h1>
        <p className="mt-2 text-sm text-[color:var(--color-text-secondary)]">
          You don't have an SFTP account on this portal yet. Accept an invitation first, or
          contact your DFX administrator if you should have access.
        </p>
      </div>
    );
  }

  const info = rotated ?? data!;

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="text-2xl font-semibold">SFTP credentials</h1>
      <p className="mt-2 text-sm text-[color:var(--color-text-secondary)]">
        Use these to connect with your SFTP client. The private key is shown only when you
        rotate — it's never stored on our servers.
      </p>

      <dl className="mt-6 grid grid-cols-[8rem_1fr] gap-y-2 text-sm">
        <dt className="text-[color:var(--color-text-secondary)]">Host</dt>
        <dd className="font-mono">{info.host ?? '—'}</dd>
        <dt className="text-[color:var(--color-text-secondary)]">Port</dt>
        <dd className="font-mono">{info.port}</dd>
        <dt className="text-[color:var(--color-text-secondary)]">Username</dt>
        <dd className="font-mono">{info.username}</dd>
        <dt className="text-[color:var(--color-text-secondary)]">Auth</dt>
        <dd>SSH key only (no password)</dd>
      </dl>

      {rotated ? (
        <NewKeyView creds={rotated} acknowledged={acknowledged} onAck={setAcknowledged} />
      ) : (
        <RotatePrompt onRotate={rotate} rotating={rotating} error={rotateError} />
      )}
    </div>
  );
}

function RotatePrompt({
  onRotate,
  rotating,
  error,
}: {
  onRotate: () => void;
  rotating: boolean;
  error: string | null;
}) {
  const [confirming, setConfirming] = useState(false);
  return (
    <div className="mt-8 rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-card)] p-4">
      <h2 className="text-base font-semibold">Lost your private key?</h2>
      <p className="mt-1 text-sm text-[color:var(--color-text-secondary)]">
        Rotate it to get a fresh keypair. Your old key will stop working immediately.
      </p>
      {!confirming ? (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="mt-3 rounded border border-[color:var(--color-border)] px-4 py-2 text-sm"
        >
          Rotate SSH key
        </button>
      ) : (
        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            onClick={onRotate}
            disabled={rotating}
            className="rounded bg-[color:var(--color-crimson)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {rotating ? 'Rotating…' : 'Yes, replace my key'}
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            disabled={rotating}
            className="rounded px-3 py-2 text-sm text-[color:var(--color-text-secondary)]"
          >
            Cancel
          </button>
        </div>
      )}
      {error ? (
        <p className="mt-3 text-sm text-[color:var(--color-warning)]">{error}</p>
      ) : null}
    </div>
  );
}

function NewKeyView({
  creds,
  acknowledged,
  onAck,
}: {
  creds: RotateResponse;
  acknowledged: boolean;
  onAck: (v: boolean) => void;
}) {
  const downloadHref = `data:application/octet-stream;charset=utf-8,${encodeURIComponent(
    creds.private_key_openssh
  )}`;
  return (
    <div className="mt-8">
      <div className="rounded border border-[color:var(--color-warning-muted)] bg-[color:var(--color-bg-card)] p-4 text-sm">
        <strong className="text-[color:var(--color-warning)]">This is shown once.</strong> The
        private key cannot be retrieved later. Download it now and store it somewhere safe.
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-[color:var(--color-text-secondary)]">
            New private key (OpenSSH)
          </h2>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(creds.private_key_openssh)}
              className="rounded border border-[color:var(--color-border)] px-3 py-1 text-xs"
            >
              Copy
            </button>
            <a
              href={downloadHref}
              download={`${creds.username}.id_ed25519`}
              className="rounded bg-[color:var(--color-crimson)] px-3 py-1 text-xs font-medium text-white"
            >
              Download
            </a>
          </div>
        </div>
        <pre className="mt-2 overflow-auto rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-primary)] p-4 font-mono text-xs leading-relaxed">
          {creds.private_key_openssh}
        </pre>
      </div>

      <div className="mt-6 rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-card)] p-4 text-sm text-[color:var(--color-text-secondary)]">
        <p className="text-[color:var(--color-text-primary)]">Quick connect</p>
        <pre className="mt-2 overflow-auto font-mono text-xs">
{`chmod 600 ${creds.username}.id_ed25519
sftp -i ${creds.username}.id_ed25519 -P ${creds.port} ${creds.username}@${creds.host}`}
        </pre>
        <p className="mt-2 text-xs">
          The <code className="font-mono">chmod 600</code> step is required — OpenSSH refuses
          private key files that other users can read.
        </p>
      </div>

      <label className="mt-6 flex items-center gap-2 text-sm">
        <input type="checkbox" checked={acknowledged} onChange={(e) => onAck(e.target.checked)} />
        I've saved the new private key.
      </label>
      {acknowledged ? (
        <p className="mt-3 text-xs text-[color:var(--color-text-secondary)]">
          Reload the page after you've saved the key — this view will be gone.
        </p>
      ) : null}
    </div>
  );
}
